import type {CombatTurnToken as Combatant} from "@/api/TurnApi.ts";
import TokenFramePreview from "./TokenFramePreview.tsx";
import {tokenFrameStyle} from "./TokenFrameStyles.ts";
import {type AreaEffectMark, visibleAreaEffectMarks} from "@/tabletop/effects/AreaEffectHighlights.ts";
import hoodFile from "@assets/token-hooded-assassin.svg";
import crownFile from "@assets/token-crown.svg";
import eyeFile from "@assets/token-outside-sight.svg";
import {initiativeArtworkHeight} from "./InitiativeLayout.ts";
import {cn} from "@/lib/utils.ts";

export default function InitiativePortrait(
    {
        combatant, size, active, selected, playerView, outsidePlayerSight,
        stacked, marks
    }: {
        combatant: Combatant; size: number; active: boolean; selected: boolean; playerView: boolean;
        outsidePlayerSight: boolean; stacked: boolean; marks: AreaEffectMark[]
    }) {
    const unknown = playerView && combatant.hidden;
    // Do not load the real portrait or expose name, personal frame, faction, health or effects for an unknown actor.
    const displayed = unknown ? {
        ...combatant, name: "Unbekannt", hidden: false, hostile: false,
        tokenFrame: "CLASSIC" as const, currentLife: 1, maxLife: 1,
        character: {...combatant.character, image: undefined}
    } : combatant;
    const artworkHeight = size;
    const health = combatant.maxLife > 0 && Number.isFinite(combatant.currentLife)
        ? Math.max(0, Math.min(1, combatant.currentLife / combatant.maxLife)) : 1;
    const showHealth = !unknown && (!playerView || !combatant.hostile);
    const visibleMarks = unknown ? [] : visibleAreaEffectMarks(marks);
    const nameTop = size * 0.9;
    return <div className="flex min-w-0 flex-col items-center">
        <div className={cn("relative flex w-full justify-center",
            unknown && "bg-[radial-gradient(circle,#15202d_45%,transparent_46%)]")}
             style={{minHeight: initiativeArtworkHeight(size, stacked)}}>
            <div aria-hidden="true" className="relative w-full shrink-0" style={{height: artworkHeight}}>
                <div className="absolute left-1/2 -translate-x-1/2" style={{width: size, top: size * 0.02}}>
                    <TokenFramePreview
                        style={tokenFrameStyle(displayed.tokenFrame)} image={displayed.character.image}
                        portraitSource={unknown ? hoodFile : undefined} health={unknown ? 1 : health}
                        className="w-full">
                        {/* Local selection remains a blue ring, visible only to the GM. */}
                        {selected && !playerView && <span
                            className="absolute rounded-full border-[#38bdf8]"
                            style={{
                                inset: -Math.max(2, size * 0.015),
                                borderWidth: Math.max(3, size * 0.02),
                                boxShadow: `0 0 ${Math.max(3, size * 0.02)}px #38bdf8`
                            }}/>}
                        {/* Dim the portrait and frame; keep foreground status icons legible. */}
                        {!playerView && outsidePlayerSight &&
                            <span className="absolute inset-0 rounded-full bg-[#020617]/30"/>}
                        {/* The turn crown occupies the upper-center foreground slot. */}
                        <img src={crownFile} alt="" draggable={false}
                             className={cn("absolute top-[5.5%] left-1/2 h-[21.07%] w-[24.5%] -translate-x-1/2 -translate-y-1/2 transition-opacity duration-[250ms] motion-reduce:transition-none",
                                 active ? "opacity-100" : "opacity-0")}
                             style={{filter: `drop-shadow(0 ${size / 75}px ${size * 0.02}px #000)`}}/>
                        {/* Hidden status uses the upper-left slot without covering the crown. */}
                        {!playerView && combatant.hidden && <span
                            className="absolute top-[15.5%] left-[18.5%] flex size-[19%] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[#8795a6] bg-[#101823]"
                            style={{borderWidth: size * 0.01}}>
                            <img src={hoodFile} alt="" draggable={false} className="size-[78%]"/>
                        </span>}
                        {/* Outside-sight status uses the separate upper-right slot. */}
                        {!playerView && outsidePlayerSight && <span
                            className="absolute top-[15.5%] left-[81.5%] flex size-[19%] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[#b6a2db] bg-[#101823]"
                            style={{borderWidth: size * 0.01}}>
                            <img src={eyeFile} alt="" draggable={false} className="size-[65%]"/>
                        </span>}
                    </TokenFramePreview>
                </div>
            </div>
            {!stacked && showHealth && <span
                className={cn("absolute left-1/2 h-[22px] -translate-x-1/2 rounded-[4px] px-[5px] py-[1px]",
                    "text-[10px] leading-[18px] whitespace-nowrap text-[#f8fafc] tabular-nums bg-[#080c14e6]")}
                style={{top: nameTop - 16}}>
                {combatant.currentLife}/{combatant.maxLife}
            </span>}
            {/* Like the map token, the name plate sits in front of the lower frame edge. */}
            {!stacked &&
                <span className={cn("absolute left-[2px] w-[calc(100%-4px)] line-clamp-2 rounded-[4px] bg-[#080c14e6]",
                    "px-[4px] py-[2px] text-center text-[11px] leading-[14px] font-semibold text-white [overflow-wrap:anywhere]")}
                      style={{top: nameTop}}>{displayed.name}</span>}

        </div>
        {!stacked && <>
            {visibleMarks.length > 0 &&
                <span className="mt-[2px] flex h-[15px] justify-center gap-[3px]">{visibleMarks.map(mark =>
                    <span key={mark.id}
                          className={cn("inline-flex h-[12px] min-w-[12px] items-center justify-center",
                              "rounded-[3px] text-[8px] font-bold text-white [text-shadow:0_1px_2px_#000,1px_0_1px_#000]")}
                          style={{backgroundColor: mark.color}}>{mark.label}</span>)}</span>}
        </>}
    </div>;
}
